import { DefaultApi } from '../base/defaultApi';
import { Singleton } from '@/decorators';

import type {
  QuestionsResponseDTO,
  IdentityResponseDTO,
  LoginResponseDTO,
  PotentialTerminationReasonsResponseDTO,
  TerminationDetailResponseDTO,
  TerminationSkippedResponseDTO,
  LoginRequestDTO,
  SetIdentityRequestDTO,
  GetIdentityRequestDTO,
  SetTerminationDetailRequestDTO,
  GetTerminationDetailRequestDTO,
  SetTerminationSkippedRequestDTO,
  GetTerminationSkippedRequestDTO,
  GetPotentialTerminationReasonsRequestDTO,
  GetQuestionsRequestDTO,
  GetCAQHInfoRequestDTO,
  GetCAQHInfoResponseDTO,
  ContinueWithCAQHRequestDTO,
  ContinueWithCAQHResponseDTO,
  GetClientPlansRequestDTO,
  GetClientPlansResponseDTO,
  DecodeRequestDTO,
  DecodeResponseDTO,
} from './DTOs';
import { generateQueryString } from '@/libs';
import type { Result } from '@/types/result.interface';
import type { AppError } from '@/types/errors.interface';
import { wrapHttpCall, wrapHttpCallWithSuccessCheck } from '../utils/apiResultMapper';
import type { HttpRequestConfig } from '@/services/http';
import {
  HINT_PATTERN,
  TURNSTILE_CLIENT_HEADER,
  TURNSTILE_TOKEN_HEADER,
  type TurnstileProof,
} from '@/services/turnstile';

/** The sign-in and invitation-link pages wait on these reads; a stalled API gets the retry screen instead of an endless spinner. */
const PAGE_LOAD_TIMEOUT_MS = 30000;

/** The request config that carries a Turnstile proof; without one the request stays exactly as it was, with no CORS preflight. */
const challengeConfig = (
  proof?: TurnstileProof,
): HttpRequestConfig | undefined => {
  if (!proof) {
    return undefined;
  }
  if ('token' in proof) {
    return { headers: { [TURNSTILE_TOKEN_HEADER]: proof.token } };
  }
  // The server logs any other hint as unrecognised, so it is not worth a custom header.
  return HINT_PATTERN.test(proof.hint)
    ? { headers: { [TURNSTILE_CLIENT_HEADER]: proof.hint } }
    : undefined;
};

/**
 * Account Controller API (Framework Agnostic)
 * 
 * Handles account-related operations including login, identity, and termination.
 * Note: This API does NOT require authentication (uses DefaultApi instead of AuthApi).
 */
@Singleton
export class AccountControllerApi extends DefaultApi {
  constructor() {
    super();
  }

  async getClientPlans(body: GetClientPlansRequestDTO): Promise<Result<GetClientPlansResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<GetClientPlansResponseDTO>(
      `/AccountController/getClientPlans?Key=${body.key}`,
      { timeout: PAGE_LOAD_TIMEOUT_MS },
    ));
  }

  async getIdentity(body: GetIdentityRequestDTO): Promise<Result<IdentityResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<IdentityResponseDTO>(
      `/AccountController/getIdentity?Key=${body.key}`,
    ));
  }

  async setIdentity(body: SetIdentityRequestDTO): Promise<Result<IdentityResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<IdentityResponseDTO>(
      `/AccountController/updateIdentity?Key=${body.key}&IsProvider=${body.isProvider}`,
    ));
  }

  async getPotentialTerminationReasons(
    body: GetPotentialTerminationReasonsRequestDTO,
  ): Promise<Result<PotentialTerminationReasonsResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<PotentialTerminationReasonsResponseDTO>(
      `/AccountController/getPotentialTerminationReasons?Key=${body.key}`,
      { timeout: PAGE_LOAD_TIMEOUT_MS },
    ));
  }

  async getTerminationDetail(body: GetTerminationDetailRequestDTO): Promise<Result<TerminationDetailResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<TerminationDetailResponseDTO>(
      `/AccountController/getTerminationDetail?Key=${body.key}`,
      { timeout: PAGE_LOAD_TIMEOUT_MS },
    ));
  }

  async setTerminationDetail(body: SetTerminationDetailRequestDTO): Promise<Result<TerminationDetailResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<TerminationDetailResponseDTO>(
      `/AccountController/terminate?Key=${body.key}`,
      body.detail,
    ));
  }

  async getTerminationSkipped(body: GetTerminationSkippedRequestDTO): Promise<Result<TerminationSkippedResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<TerminationSkippedResponseDTO>(
      `/AccountController/getTerminationSkipped?Key=${body.key}`,
      { timeout: PAGE_LOAD_TIMEOUT_MS },
    ));
  }

  async setTerminationSkipped(body: SetTerminationSkippedRequestDTO): Promise<Result<TerminationSkippedResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<TerminationSkippedResponseDTO>(
      `/AccountController/skipTermination?Key=${body.key}`,
    ));
  }

  async getQuestions(body: GetQuestionsRequestDTO): Promise<Result<QuestionsResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<QuestionsResponseDTO>(
      `/AccountController/getQuestions?Key=${body.key}`,
      { timeout: PAGE_LOAD_TIMEOUT_MS },
    ));
  }

  async login(body: LoginRequestDTO): Promise<Result<LoginResponseDTO, AppError>> {
    // The names travel in the body; URLs end up in telemetry and access logs.
    const queryParams = generateQueryString({ Key: body.key });

    // Login uses ResultDTO with success property and error arrays
    return wrapHttpCallWithSuccessCheck(() => 
      this.api.post<LoginResponseDTO>(
        `/AccountController/login${queryParams}`,
        {
          answers: body.answers,
          firstName: body.userData.firstName,
          lastName: body.userData.lastName,
        },
        challengeConfig(body.challenge),
      )
    );
  }

  async getCaqhInfo(body: GetCAQHInfoRequestDTO): Promise<Result<GetCAQHInfoResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<GetCAQHInfoResponseDTO>(
      `/AccountController/getCAQHInfo?Key=${body.key}`,
      { timeout: PAGE_LOAD_TIMEOUT_MS },
    ));
  }

  async continueWithCAQH(body: ContinueWithCAQHRequestDTO): Promise<Result<ContinueWithCAQHResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<ContinueWithCAQHResponseDTO>(
      `/AccountController/continueCAQH?Key=${body.key}`,
      body.continuationUser,
    ));
  }

  async decode(body: DecodeRequestDTO): Promise<Result<DecodeResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<DecodeResponseDTO>(
      `/AccountController/decode/${body.code}`,
      { timeout: PAGE_LOAD_TIMEOUT_MS, ...challengeConfig(body.challenge) },
    ));
  }
}
